import { ComponentType } from '@/features/transaction_items/List/type';
import { useTranslation } from '@/i18n';
import {
  FormDatepicker,
  FormInput,
  Select,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import styles from './ComponentPreview.module.scss';
import { PreviewContentWrapper } from './PreviewContent/PreviewContentWrapper';

const cx = classNames.bind(styles);

type PreviewFormType = {
  preview_text?: string;
  preview_alphanumeric?: string;
  preview_numeric?: string;
  placeholder?: string;
};

type PreviewBodyProps = {
  placeholder?: string;
};

type InputPreviewBodyProps = PreviewBodyProps & {
  name: 'preview_text' | 'preview_alphanumeric' | 'preview_numeric';
  inputId: string;
  testId: string;
};

const InputPreviewBody: FC<InputPreviewBodyProps> = ({
  placeholder,
  name,
  inputId,
  testId,
}) => {
  const { control } = useForm<PreviewFormType>({
    defaultValues: {
      preview_text: '',
      preview_alphanumeric: '',
      preview_numeric: '',
    },
  });
  const inputProps = useMemo(
    () => ({ placeholder, readOnly: true }),
    [placeholder]
  );

  return (
    <FormInput
      name={name}
      control={control}
      inputId={inputId}
      inputProps={inputProps}
      testId={testId}
    />
  );
};

const DatePreviewBody: FC<PreviewBodyProps> = ({}) => {
  const { control } = useForm<PreviewFormType>();

  const datepickerProps = useMemo(() => {
    const baseProps = {
      inputReadOnly: true,
      disabled: true,
    };
    return baseProps;
  }, []);

  return (
    <FormDatepicker<PreviewFormType>
      name='placeholder'
      control={control}
      inputId='preview-date'
      testId='preview-date'
      single
      format='YYYY-MM-DD'
      datepickerProps={datepickerProps}
    />
  );
};

const PulldownPreviewBody: FC = () => {
  const options = useMemo(() => [], []);

  return <Select options={options} />;
};

export type Props = {
  componentType: ComponentType;
  label?: string;
  placeholder?: string;
  tooltip?: string;
};

export const ComponentPreview: FC<Props> = ({
  componentType,
  label,
  placeholder,
  tooltip,
}) => {
  const { t } = useTranslation();

  const PreviewComponent = useMemo(() => {
    if (!componentType) return null;

    const commonProps = {
      label,
      tooltip,
    };

    switch (componentType) {
      case 'text':
        return (
          <PreviewContentWrapper
            {...commonProps}
            contentClassName='preview-content-text'
          >
            <InputPreviewBody
              placeholder={placeholder}
              name='preview_text'
              inputId='preview-text'
              testId='preview-text'
            />
          </PreviewContentWrapper>
        );
      case 'alphanumeric':
        return (
          <PreviewContentWrapper
            {...commonProps}
            contentClassName='preview-content-alphanumeric'
          >
            <InputPreviewBody
              placeholder={placeholder}
              name='preview_alphanumeric'
              inputId='preview-alphanumeric'
              testId='preview-alphanumeric'
            />
          </PreviewContentWrapper>
        );
      case 'numeric':
        return (
          <PreviewContentWrapper
            {...commonProps}
            contentClassName='preview-content-numeric'
          >
            <InputPreviewBody
              placeholder={placeholder}
              name='preview_numeric'
              inputId='preview-preview_numeric'
              testId='preview-preview_numeric'
            />
          </PreviewContentWrapper>
        );
      case 'date':
        return (
          <PreviewContentWrapper
            {...commonProps}
            contentClassName='preview-content-date'
          >
            <DatePreviewBody placeholder={placeholder} />
          </PreviewContentWrapper>
        );
      case 'pulldown':
        return (
          <PreviewContentWrapper
            {...commonProps}
            contentClassName='preview-content-pulldown'
          >
            <PulldownPreviewBody />
          </PreviewContentWrapper>
        );
      default:
        return null;
    }
  }, [componentType, label, placeholder, tooltip]);

  if (!PreviewComponent) {
    return null;
  }

  return (
    <div className={cx(styles['preview-section'])}>
      <div className={cx(styles['preview-section-title'])}>
        {t('transaction_items.drawer.preview_section.title')}
      </div>
      <p className={cx(styles['preview-section-description'])}>
        {t('transaction_items.drawer.preview_section.description')}
      </p>
      <div className={cx(styles['preview-content-wrapper'])}>
        {PreviewComponent}
      </div>
    </div>
  );
};

ComponentPreview.displayName = 'ComponentPreview';
